import _ from 'lodash';
import { faker } from '@faker-js/faker';
import * as z from 'zod';
import toast from 'react-hot-toast';

// This is used to remove the `id` field from metadata items
export const convertMetadata = (metadata) => {
  return metadata.reduce((acc, item) => {
    acc[item.key] = item.value;
    return acc;
  }, {});
};

export const sanitiseContent = (content) => {
  if (!content || !Array.isArray(content)) {
    return [];
  }

  return content.map((item) => {
    const { propSchema, label, component_category, component_origin, ...rest } = item;
    return rest;
  });
};

export const formatModulesConnected = (dynamicPageModules, originalDynamicPageModules = []) => {
  // Create a map for quick lookup of original modules by prop_field_name
  const originalModulesMap = new Map(originalDynamicPageModules.map((mod) => [mod.prop_field_name, mod]));

  // Create an array to hold the formatted module assignments
  const formattedModules = [];

  // Process dynamicPageModules to handle new and updated modules
  dynamicPageModules.forEach((module) => {
    const originalModule = originalModulesMap.get(module.prop_field_name);

    if (originalModule) {
      // If the module exists in both arrays but the dynamic_page_module_id has changed
      if (originalModule.dynamic_page_module_id !== module.dynamic_page_module_id) {
        formattedModules.push({
          id: originalModule.id,
          dynamic_page_module_id: module.dynamic_page_module_id,
          prop_field_name: module.prop_field_name,
        });
      } else {
        // If the module exists in both arrays and the dynamic_page_module_id has not changed
        formattedModules.push({ id: originalModule.id });
      }
      // Remove the processed module from the map
      originalModulesMap.delete(module.prop_field_name);
    } else {
      // If the module exists in dynamicPageModules but not in originalDynamicPageModules
      formattedModules.push({
        dynamic_page_module_id: module.dynamic_page_module_id,
        prop_field_name: module.prop_field_name,
      });
    }
  });

  // Process remaining original modules to handle deletions
  originalModulesMap.forEach((originalModule) => {
    formattedModules.push({
      id: originalModule.id,
      _destroy: true,
    });
  });

  return formattedModules;
};

const validTypesSchemas = {
  string: z.string(),
  boolean: z.boolean(),
  array: z.array(z.any()),
  number: z.number(),
  template: z.array(z.any()),
  formatted_text: z.string(),
  object: z.object({}).refine((val) => !Array.isArray(val), {
    message: 'Value must not be an array',
  }),
  file: z.object({
    id: z.string(),
    url: z.string(),
    title: z.string().optional(),
    alt: z.string().optional(),
  }),
  repeater: z.array(z.any()),
  range: z.number(),
};

const isValidType = (value, type) => {
  const schema = validTypesSchemas[type];
  if (!schema) return false;
  try {
    schema.parse(value);
    return true;
  } catch {
    return false;
  }
};

export const typeBasedDefaultValue = (prop, defaultValue) => {
  if (defaultValue !== undefined && isValidType(defaultValue, prop.type)) {
    if (prop.type === 'file') {
      const { id, url, title, alt } = defaultValue || {};
      return { id, url, title, alt };
    }

    return defaultValue;
  }

  switch (prop.type) {
    case 'string':
      return '';
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'number':
      return 0;
    case 'template':
      return [];
    case 'repeater':
      return [];
    case 'formatted_text':
      return '';
    case 'object':
      if (!Array.isArray(prop.object_schema)) return {};

      return prop.object_schema.reduce((acc, field) => {
        acc[field.name] = typeBasedDefaultValue(field, field.defaultValue);
        return acc;
      }, {});
    case 'range':
      return 0;
    default:
      return null;
  }
};

export const isValidJSON = (str) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const isTemplateString = (str) => {
  return typeof str === 'string' && str.startsWith('{{') && str.endsWith('}}');
};

export const omitKeys = (obj, keys) => {
  if (!obj) return {};
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
};

export const generateFullDefinitions = (componentDefinitions, isTemplate) => {
  if (isTemplate) {
    return [
      ...(componentDefinitions || []), // Include the usual component definitions
      // Add a special definition for templates here, only available for templates
      {
        label: 'Rest of page content',
        component: 'children',
        component_category: 'for_template_layout',
      },
    ];
  }

  return componentDefinitions;
};

const ALLOWED_DEFAULT_KEYWORDS = [
  'style',
  'css',
  'class',
  'colour',
  'color',
  'background',
  'alignment',
  'margin',
  'padding',
  'width',
  'height',
  'border',
  'font',
  'text',
  'url',
  'link',
  'href',
  'target',
  'rel',
  'video',
  'image',
  'icon',
  'logo',
  'footer',
  'header',
  'items',
  'size',
  'hover',
  'opacity',
  'blur',
  'duration',
  'transition',
  'rounded',
  'shadow',
  'weight',
];

const ALLOWED_TEMPLATE_KEYWORDS = [...ALLOWED_DEFAULT_KEYWORDS, 'title', 'label', 'id'];

const allowedDefaultSet = new Set(ALLOWED_DEFAULT_KEYWORDS);
const allowedTemplateSet = new Set(ALLOWED_TEMPLATE_KEYWORDS);

function containsKeyword(name, keywordsSet) {
  const lowerName = name.toLowerCase();
  for (const keyword of keywordsSet) {
    if (lowerName.includes(keyword)) {
      return true;
    }
  }
  return false;
}

const generateRandomDataForProp = (prop, isDataTypeTemplate = false) => {
  const { type, defaultValue, template_schema, object_schema, name, rangeProps } = prop;

  const shouldUseDefault = isDataTypeTemplate ? containsKeyword(name, allowedTemplateSet) : containsKeyword(name, allowedDefaultSet);

  if (defaultValue !== undefined && shouldUseDefault && isValidType(defaultValue, type)) {
    if (type === 'file') {
      const { id, url, title, alt } = defaultValue || {};
      return { id, url, title, alt };
    }
    return defaultValue;
  }

  switch (type) {
    case 'string':
      return faker.company.catchPhrase();
    case 'boolean':
      return faker.datatype.boolean();
    case 'array':
      return [];
    case 'number':
      return faker.number.int({ min: 1, max: 3 });
    case 'template':
      if (!Array.isArray(template_schema)) return [];

      return Array.from({ length: 3 }, () =>
        template_schema.reduce((acc, field) => {
          acc[field.name] = generateRandomDataForProp(field, true);
          return acc;
        }, {}),
      );
    case 'repeater':
      return [
        { id: faker.string.uuid(), content: faker.lorem.text() },
        { id: faker.string.uuid(), content: faker.lorem.text() },
        { id: faker.string.uuid(), content: faker.lorem.text() },
      ];
    case 'formatted_text':
      return `<p>${faker.lorem.sentences({ min: 2, max: 5 })}</p>`;
    case 'file':
      return {
        id: faker.string.uuid(),
        url: 'https://placehold.co/600x400',
        title: faker.lorem.sentence(),
        alt: faker.lorem.sentences(),
      };
    case 'object':
      if (!Array.isArray(object_schema)) return {};
      return object_schema.reduce((acc, field) => {
        acc[field.name] = generateRandomDataForProp(field, true);
        return acc;
      }, {});
    case 'range':
      return faker.number.int({ min: rangeProps.min, max: rangeProps.max });
    default:
      return null;
  }
};

export const generateProps = (component) => {
  const { propSchema } = component;

  if (!propSchema) return {};

  return propSchema.reduce((acc, prop) => {
    if (!prop || !prop.name || !prop.type) return acc;

    acc[prop.name] = generateRandomDataForProp(prop);
    return acc;
  }, {});
};

export const generateBlankProps = (propSchema) => {
  return propSchema.reduce((acc, prop) => {
    if (!prop) {
      toast.error('This component has a null or undefined prop in the prop schema.');
      return acc;
    }
    if (!prop.name) {
      toast.error('This component has a missing prop name in the prop schema.');
      return acc;
    }
    if (!prop.type) {
      toast.error('This component has a missing prop type in the prop schema.');
      return acc;
    }

    acc[prop.name] = typeBasedDefaultValue(prop, prop.defaultValue);
    return acc;
  }, {});
};

export const VIEWPORT_OPTIONS = [
  { name: 'Full', className: 'full-size', icon: 'fe fe-maximize' },
  { name: 'Desktop', className: 'desktop', icon: 'fe fe-monitor' },
  { name: 'Tablet', className: 'tablet', icon: 'fe fe-tablet' },
  { name: 'Mobile', className: 'mobile', icon: 'fe fe-smartphone' },
];

export const ERRORS = [
  { key: 'title_empty', message: 'Title is required!' },
  { key: 'url_slug_empty', message: 'URL Slug is required!' },
  { key: 'url_slug_invalid', message: 'URL Slug cannot start or end with a slash or contain spaces!' },
];

export const VALID_PROP_FIELD_TYPES = [
  'string',
  'boolean',
  'array',
  'number',
  'formatted_text',
  'file',
  'template',
  'forms',
  'modules',
  'object',
  'repeater',
  'range',
];
export const VALID_TEMPLATE_PROP_FIELD_TYPES = [
  'string',
  'boolean',
  'array',
  'number',
  'formatted_text',
  'file',
  'template',
  'forms',
  'modules',
  'object',
  'repeater',
  'range',
];

export const COMPONENT_IDENTIFIER_KEYWORD = 'component';
export const COMPONENT_LABEL_KEYWORD = 'label';
export const COMPONENT_NAME_KEYWORD = 'name';

export const OMIT_KEYS_NEW = ['modules_connected', 'assigned_hreflang_group', 'assigned_category'];

export const OMIT_KEYS_EDIT = [
  'site_id',
  'created_at',
  'updated_at',
  'is_dynamic_page',
  'is_from_sitemap',
  'parent_category_id',
  'modules_connected',
  'dynamic_page_template_modules',
  'assigned_hreflang_group',
  'assigned_category',
];

export const OMIT_KEYS_NEW_MODULE = ['metadata', 'seo', 'url_slug', 'modules_connected'];

export const OMIT_KEYS_EDIT_MODULE = ['site_id', 'created_at', 'updated_at', 'metadata', 'seo', 'url_slug', 'modules_connected'];

export const BARRED_DUPLICATE_COMPONENTS = ['children'];

export const PREVIEW_SOURCE = 'components';
export const PREVIEW_NO_LAYOUT_SOURCE = 'components-standalone';

export const ZOOM_LEVELS = [
  { value: 0.5, label: '50%' },
  { value: 0.66, label: '66%' },
  { value: 0.75, label: '75%' },
  { value: 0.85, label: '85%' },
  { value: 1, label: '100%' },
];

export const IFRAME_ELEMENT_ID = 'sf-builder-preview';
